/* Copyright (c) 2025-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import {
    Component,
    Input,
    Output,
    EventEmitter,
    ElementRef,
    ViewChild,
    OnChanges,
    SimpleChanges,
    OnInit,
    AfterViewInit
} from '@angular/core';
import { AnimationBuilder, animate, style } from '@angular/animations';

import { FlexModule } from '@angular/flex-layout/flex';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { A11yModule, LiveAnnouncer } from '@angular/cdk/a11y';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LocaleService } from 'crimson/lib/locale-service/locale.service';
import { EllipsifiedTextWithTooltipDirective } from '../../directives/ellipsified-text-with-tooltip.directive';

@Component({
    selector: 'nv-hig-rotary',
    templateUrl: './nv-hig-rotary.component.html',
    styleUrls: ['./nv-hig-rotary.component.scss'],
    imports: [
        A11yModule,
        MatButtonModule,
        MatIconModule,
        FlexModule,
        TranslateModule,
        EllipsifiedTextWithTooltipDirective
    ]
})
export class NvHigRotaryComponent implements OnChanges, OnInit, AfterViewInit {
    @ViewChild('button') private element: ElementRef;

    @Input()
    set input(list: string[]) {
        this._input = list;
        this.displayList = list.slice();
    }
    get input(): string[] {
        return this._input;
    }
    private _input: string[];

    @Input() disabled = false;
    @Input() truncateText = false;
    @Input() applyEllipsisStyles = false;
    @Input()
    set initialState(initState: number) {
        this._initialState = initState;
        if (this.initialized && initState !== this.selectedIndex) {
            this.ngOnInit();
            this.ngAfterViewInit();
        }
    }
    get initialState(): number {
        return this._initialState;
    }
    private _initialState = 0;

    @Input() continuous = true;
    @Output() selectionChanged = new EventEmitter<number>();

    @Input() leftButtonAriaLabel?: string;
    @Input() rightButtonAriaLabel?: string;
    @Input() set ariaLabel(value: string) {
        this._ariaLabel = value;
        this.computedAriaLabel = value ? `${value}. ${this.translate.instant('common.rotaryInstructions')}` : null;
    }
    private _ariaLabel: string;
    computedAriaLabel: string | null = null;

    @ViewChild('container', { static: true }) private container: ElementRef;
    @ViewChild('items', { static: true }) private itemContainer: ElementRef;

    displayList: string[];
    private displayIndex = 0;

    selectedIndex = -1;

    private initialized = false;
    private isRTLLayout = false;

    constructor(
        private animationBuilder: AnimationBuilder,
        private liveAnnouncer: LiveAnnouncer,
        private translate: TranslateService,
        localeService: LocaleService
    ) {
        this.isRTLLayout = localeService.isRTLLayoutEnabled;
    }

    private announceCurrentValue() {
        const value = this.input?.[this.selectedIndex];
        if (!value) return;
        this.liveAnnouncer.announce(this.translate.instant(value), 'polite');
    }

    private moveToLeft() {
        if ((this.continuous === false && this.selectedIndex === 0) || this.disabled || this.input.length === 0) {
            return;
        }
        if (this.displayIndex === 0) {
            const last = this.displayList.pop();
            this.displayList.unshift(last);
            this.displayIndex = 1;
            this.animateTo(this.displayIndex, true);
        }
        this.displayIndex--;
        this.selectedIndex = (this.input.length + this.selectedIndex - 1) % this.input.length;
        this.selectionChanged.emit(this.selectedIndex);
        this.animateTo(this.displayIndex, false);
        this.announceCurrentValue();
    }

    private moveToRight() {
        if (
            (this.continuous === false && this.selectedIndex === this.input.length - 1) ||
            this.disabled ||
            this.input.length === 0
        ) {
            return;
        }
        if (this.displayIndex + 1 === this.displayList.length) {
            const first = this.displayList.shift();
            this.displayList.push(first);
            this.displayIndex--;
            this.animateTo(this.displayIndex, true);
        }
        this.displayIndex++;
        this.selectedIndex = (this.selectedIndex + 1) % this.input.length;
        this.selectionChanged.emit(this.selectedIndex);
        this.animateTo(this.displayIndex, false);
        this.announceCurrentValue();
    }

    goLeft(event: KeyboardEvent | MouseEvent) {
        event?.preventDefault();
        this.isRTLLayout ? this.moveToRight() : this.moveToLeft();
    }

    goRight(event: KeyboardEvent | MouseEvent) {
        event?.preventDefault();
        this.isRTLLayout ? this.moveToLeft() : this.moveToRight();
    }

    ngOnChanges(changes: SimpleChanges) {
        for (const propName of Object.keys(changes)) {
            if (propName === 'input') {
                if (changes[propName].currentValue.length - 1 < this.initialState) {
                    this.initialState = 0;
                }
                if (this.initialState === 0 || this.initialState === this.input.length - 1) {
                    this.ngOnInit();
                    this.ngAfterViewInit();
                }
            }
        }
    }

    ngOnInit() {
        this.displayIndex = this.selectedIndex = this.initialState;
        if (this.input) {
            this.displayList = this.input.slice();
        } else {
            this.displayList = undefined;
        }
    }

    ngAfterViewInit() {
        this.animateTo(this.displayIndex, true);
        this.initialized = true;
    }

    private animateTo(index: number, instant: boolean) {
        let offset = index * this.container.nativeElement.offsetWidth;
        if (!this.isRTLLayout) {
            offset = offset * -1;
        }
        const animation = this.animationBuilder.build([
            animate(
                !instant ? '250ms cubic-bezier(0.4, 0.0, 0.2, 1)' : 0,
                style({ transform: `translateX(${offset}px)` })
            )
        ]);
        const player = animation.create(this.itemContainer.nativeElement);
        player.play();
    }

    focus() {
        this.element.nativeElement.focus();
    }
}
